import { Injectable } from '@angular/core'
import { LocalMediaFile, LogService } from 'htv-lib'
import { MonitoringMemoryData, MonitoringStorageData } from '../interfaces/monitoring'
import { PowerState } from '../interfaces/power-state'
import { NetworkConnectionType, NetworkData, NetworkIpMode, NetworkState } from '../interfaces/network'
import { ChannelPosition } from '../pages/channel/channel-position'
import { DVBTChannel, UDPChannel, WebChannel } from '../interfaces/channel'
import { BehaviorSubject } from 'rxjs'
import { safeStringify } from '../utils/safe-stringify'
import { WebosIdcapExternalInputList } from './webos-idcap-types'
import { ExternalInputData, ExternalInputType } from '../services/external-input.service'

declare const idcap: any | undefined

@Injectable({ providedIn: 'root' })
export class WebOSIDCAP {
  private readonly MEDIA_DIR = 'file://internal/medias'
  private readonly MEDIA_PATH = 'http://127.0.0.1:8151/__internal__/medias'

  constructor(private logService: LogService) {
    // do nothing
  }

  // For LG remote control, keycode should be 0
  // https://procentric.developer.lge.com/docs/hcap-idcap-app-development/handling-keys
  // https://procentric.developer.lge.com/api-references/idcap-api/system#idcapsystemkeyadd
  registerKey(keycode: number, virtualKeycode: string): void {
    idcap.request('idcap://system/key/add', {
      parameters: {
        keycode: keycode,
        virtualKeycode: virtualKeycode,
        attribute: 2,
      },
      onSuccess: () => {
        this.logService.debug(`[registerKey] Registered key ${virtualKeycode} (${keycode})`)
      },
      onFailure: (err: any) => {
        this.logService.error(`[registerKey] ${err?.errorMessage}`)
      },
    })
  }

  getBrowserDebugMode(): Promise<boolean> {
    return new Promise((resolve, reject) => {
      idcap.request('idcap://configuration/property/get', {
        parameters: {
          key: 'browser_debug_mode',
        },
        onSuccess: (obj: any) => {
          resolve(obj?.value == '1')
        },
        onFailure: (err: any) => {
          this.logService.error(`[browser_debug_mode] ${err?.errorMessage}`)
          reject()
        },
      })
    })
  }

  setBrowserDebugMode(enable: boolean): void {
    idcap.request('idcap://configuration/property/set', {
      parameters: {
        key: 'browser_debug_mode',
        value: enable ? '1' : '0',
      },
      onSuccess: () => {
        this.logService.info(`[setBrowserDebugMode] Debug mode: ${enable}`)
      },
      onFailure: (err: any) => {
        this.logService.error(`[setBrowserDebugMode] ${err?.errorMessage}`)
      },
    })
  }

  setIdcapMode(): void {
    idcap.request('idcap://configuration/idcapmode/set', {
      parameters: {
        mode: 1,
      },
      onSuccess: () => {
        this.logService.info(`[setIdcapMode] IDCAP mode: 1`)
      },
      onFailure: (err: any) => {
        this.logService.error(`[setIdcapMode] ${err?.errorMessage}`)
      },
    })
  }

  getHotelMode(): Promise<string> {
    return new Promise((resolve, reject) => {
      idcap.request('idcap://configuration/hotelmode/get', {
        parameters: {},
        onSuccess: (obj: any) => {
          resolve(obj.hotelMode)
        },
        onFailure: (err: any) => {
          this.logService.error(`[getHotelMode] ${err?.errorMessage}`)
          reject()
        },
      })
    })
  }

  setHotelMode(hotelMode: any): Promise<void> {
    return new Promise((resolve, reject) => {
      idcap.request('idcap://configuration/hotelmode/set', {
        parameters: {
          hotelMode: JSON.stringify(hotelMode),
        },
        onSuccess: () => {
          this.logService.info(`[setHotelMode] Hotel mode configured`)
          resolve()
        },
        onFailure: (err: any) => {
          this.logService.error(`[setHotelMode] ${err?.errorMessage}`)
          reject()
        },
      })
    })
  }

  setProperty(key: string, value: string): Promise<void> {
    return new Promise((resolve, reject) => {
      idcap.request('idcap://configuration/property/set', {
        parameters: {
          key: key,
          value: value,
        },
        onSuccess: () => {
          this.logService.debug(`[${key}] ${value}`)
          resolve()
        },
        onFailure: (err: any) => {
          this.logService.error(`[${key}] ${err?.errorMessage}`)
          reject()
        },
      })
    })
  }

  setDisplayResolution(value: string): void {
    this.setProperty('display_resolution', value)
  }

  setTVChannelFloatingUI(value: string): void {
    this.setProperty('tv_channel_attribute_floating_ui', value)
  }

  setBootSequenceOption(value: string): void {
    this.setProperty('boot_sequence_option', value)
  }

  setInstantPowerOnMode(value: string): void {
    this.setProperty('instant_power', value)
  }

  setApplicationChannelControl(value: string): void {
    this.setProperty('application_channel_control', value)
  }

  setTvVolumeUI(value: string): void {
    this.setProperty('tv_volume_ui', value)
  }

  setDefaultSubtitleLanguage(value: string): void {
    this.setProperty('default_subtitle_language', value)
  }

  setDefaultAudioLanguage(value: string): void {
    this.setProperty('default_audio_language', value)
  }

  setSecurityLevel(value: string): Promise<void> {
    return new Promise((resolve, reject) => {
      idcap.request('idcap://configuration/property/set', {
        parameters: {
          key: 'security_level',
          value: value,
        },
        onSuccess: () => {
          this.logService.debug(`[security_level] ${value}`)
          resolve()
        },
        onFailure: (err: any) => {
          this.logService.error(`[security_level] ${err?.errorMessage}`)
          reject()
        },
      })
    })
  }

  getStringProperty(key: string): Promise<string> {
    return new Promise((resolve, reject) => {
      idcap.request('idcap://configuration/property/get', {
        parameters: {
          key: key,
        },
        onSuccess: (obj: any) => {
          resolve(obj.value)
        },
        onFailure: (err: any) => {
          this.logService.error(`[${key}] ${err?.errorMessage}`)
          reject()
        },
      })
    })
  }

  getModel(): Promise<string> {
    return this.getStringProperty('model_name')
  }

  getManufacturer(): Promise<string> {
    return this.getStringProperty('platform_manufacturer')
  }

  getSerialNumber(): Promise<string> {
    return this.getStringProperty('serial_number')
  }

  getFirmwareVersion(): Promise<string> {
    return this.getStringProperty('firmware_version')
  }

  getXaitVersion(): Promise<string> {
    return this.getStringProperty('xait_version')
  }

  getCpuUsage(): Promise<number> {
    return new Promise((resolve, reject) => {
      idcap.request('idcap://system/usage/get', {
        parameters: {
          keys: ['cpu'],
        },
        onSuccess: (obj: any) => {
          resolve(obj.cpuUsage)
        },
        onFailure: (err: any) => {
          this.logService.error(`[getCpuUsage] ${err?.errorMessage}`)
          reject()
        },
      })
    })
  }

  getMemoryUsage(): Promise<MonitoringMemoryData> {
    return new Promise((resolve, reject) => {
      idcap.request('idcap://system/usage/get', {
        parameters: {
          keys: ['memory'],
        },
        onSuccess: (obj: any) => {
          resolve({
            availableMemory: obj.freememory,
            totalMemory: obj.totalmemory,
          })
        },
        onFailure: (err: any) => {
          this.logService.error(`[getMemoryUsage] ${err?.errorMessage}`)
          reject()
        },
      })
    })
  }

  getStorageData(): Promise<MonitoringStorageData[]> {
    return new Promise((resolve, reject) => {
      idcap.request('idcap://storage/info/get', {
        parameters: {},
        onSuccess: (obj: any) => {
          resolve([
            {
              type: 'internal',
              availableCapacity: obj.internal.free,
              capacity: obj.internal.total,
              isRemovable: false,
            },
          ])
        },
        onFailure: (err: any) => {
          this.logService.error(`[getStorageData] ${err?.errorMessage}`)
          reject()
        },
      })
    })
  }

  reboot(): void {
    idcap.request('idcap://power/command', {
      parameters: {
        powerCommand: 'reboot',
      },
      onSuccess: () => {
        this.logService.info(`[reboot] success`)
      },
      onFailure: (err: any) => {
        this.logService.error(`[reboot] ${err?.errorMessage}`)
      },
    })
  }

  getPowerState(): Promise<PowerState | undefined> {
    return new Promise(resolve => {
      idcap.request('idcap://power/powermode/get', {
        parameters: {},
        onSuccess: (obj: any) => {
          const powerState = obj.mode == 'NORMAL' ? PowerState.NORMAL : PowerState.STANDBY
          resolve(powerState)
        },
        onFailure: (err: any) => {
          this.logService.error(`[getPowerState] ${err?.errorMessage}`)
          resolve(undefined)
        },
      })
    })
  }

  setPowerState(state: PowerState): void {
    idcap.request('idcap://power/powermode/set', {
      parameters: {
        mode: state == PowerState.STANDBY ? 'WARM' : 'NORMAL',
      },
      onSuccess: () => {
        this.logService.info(`[setPowerState] success`)
      },
      onFailure: (err: any) => {
        this.logService.error(`[setPowerState] ${err?.errorMessage}`)
      },
    })
  }

  getNetworkState(): Promise<NetworkState> {
    return new Promise(resolve => {
      idcap.request('idcap://network/configuration/get', {
        parameters: {},
        onSuccess: (obj: any) => {
          const networkState = obj.isInternetConnectionAvailable ? NetworkState.CONNECTED : NetworkState.DISCONNECTED
          resolve(networkState)
        },
        onFailure: (err: any) => {
          this.logService.error(`[getNetworkState] ${err?.errorMessage}`)
          resolve(NetworkState.UNKNOWN)
        },
      })
    })
  }

  getNetworkData(): Promise<NetworkData> {
    return new Promise((resolve, reject) => {
      idcap.request('idcap://network/configuration/get', {
        parameters: {},
        onSuccess: (obj: any) => {
          if (obj.wifi && obj.wifi.state == 'connected') {
            const wifi = obj.wifi
            const networkData: NetworkData = {
              type: NetworkConnectionType.WIFI,
              macAddress: wifi.mac,
              ipAddress: wifi.ipAddress,
              ipMode: wifi.dhcp ? NetworkIpMode.DYNAMIC : NetworkIpMode.UNKNOWN,
              subnetMask: wifi.netmask,
              gateway: wifi.gateway,
              primaryDns: wifi.dns1,
              secondaryDns: wifi.dns2,
              proxy: '',
              wifi: {
                ssid: wifi.ssid,
                signalStrengthLevel: 0,
                securityMode: '',
                encryptionType: '',
                frequency: undefined,
              },
            }
            resolve(networkData)
          } else if (obj.wired && obj.wired.state == 'connected') {
            const wired = obj.wired
            const networkData: NetworkData = {
              type: NetworkConnectionType.ETHERNET,
              macAddress: wired.mac,
              ipAddress: wired.ipAddress,
              ipMode: wired.dhcp ? NetworkIpMode.DYNAMIC : NetworkIpMode.UNKNOWN,
              subnetMask: wired.netmask,
              gateway: wired.gateway,
              primaryDns: wired.dns1,
              secondaryDns: wired.dns2,
              proxy: '',
            }
            resolve(networkData)
          } else {
            reject()
          }
        },
        onFailure: (err: any) => {
          this.logService.error(`[getNetworkData] ${err?.errorMessage}`)
          reject()
        },
      })
    })
  }

  getSoftAPConfiguration(): Promise<any> {
    return new Promise((resolve, reject) => {
      idcap.request('idcap://network/softap/get', {
        parameters: {},
        onSuccess: (obj: any) => {
          resolve({
            enabled: obj.enabled,
            ssid: obj.ssid,
            securityKey: obj.securityKey,
          })
        },
        onFailure: (err: any) => {
          this.logService.error(`[getSoftAPConfiguration] ${err?.errorMessage}`)
          reject()
        },
      })
    })
  }

  disableSoftAP(): Promise<void> {
    return new Promise((resolve, reject) => {
      idcap.request('idcap://network/softap/set', {
        parameters: {
          enabled: false,
        },
        onSuccess: () => {
          this.logService.info(`[disableSoftAP] success`)
          resolve()
        },
        onFailure: (err: any) => {
          this.logService.error(`[disableSoftAP] ${err?.errorMessage}`)
          reject()
        },
      })
    })
  }

  fileExist(path: string): Promise<boolean> {
    return new Promise((resolve, reject) => {
      idcap.request('idcap://storage/file/exist', {
        parameters: {
          path: path,
        },
        onSuccess: (obj: any) => {
          this.logService.debug(`[fileExist] ${path}: ${obj.exists}`)
          resolve(obj.exists)
        },
        onFailure: (err: any) => {
          this.logService.error(`[fileExist] ${err?.errorMessage}`)
          reject()
        },
      })
    })
  }

  createDirectory(path: string): Promise<void> {
    return new Promise((resolve, reject) => {
      idcap.request('idcap://storage/file/mkdir', {
        parameters: {
          path: path,
        },
        onSuccess: () => {
          this.logService.debug('[createDirectory] success')
          resolve()
        },
        onFailure: (err: any) => {
          this.logService.error(`[createDirectory] ${err?.errorMessage}`)
          reject()
        },
      })
    })
  }

  listFiles(path: string): Promise<LocalMediaFile[]> {
    return new Promise((resolve, reject) => {
      idcap.request('idcap://storage/file/list', {
        parameters: {
          path: path,
        },
        onSuccess: (obj: any) => {
          if (obj.fileList) {
            resolve(
              obj.fileList.map((file: any) => ({
                id: file.name.substring(0, file.name.lastIndexOf('.')) || file.name,
                filename: file.name,
                url: `${this.MEDIA_PATH}/${file.name}`,
                sizeInBytes: file.size,
                sha1: undefined,
                md5: undefined,
              }))
            )
          } else {
            resolve([])
          }
        },
        onFailure: (err: any) => {
          this.logService.error(`[listFiles] ${err?.errorMessage}`)
          reject()
        },
      })
    })
  }

  getLocalMediaFile(id: string, fileExtension: string): Promise<LocalMediaFile> {
    const filename = `${id}.${fileExtension}`
    const path = `${this.MEDIA_DIR}/${filename}`
    return new Promise((resolve, reject) => {
      idcap.request('idcap://storage/file/stat', {
        parameters: {
          path: path,
        },
        onSuccess: (obj: any) => {
          resolve({
            id: id,
            filename: filename,
            url: `${this.MEDIA_PATH}/${filename}`,
            sizeInBytes: obj.size,
            sha1: undefined,
            md5: undefined,
          })
        },
        onFailure: (err: any) => {
          this.logService.error(`[startDownload] ${err?.errorMessage}`)
          reject()
        },
      })
    })
  }

  startDownload(source: string, destination: string): Promise<number> {
    return new Promise((resolve, reject) => {
      idcap.request('idcap://storage/file/download', {
        parameters: {
          action: 'start',
          source: source,
          destination: destination,
        },
        onSuccess: (obj: any) => {
          resolve(obj.ticket)
        },
        onFailure: (err: any) => {
          this.logService.error(`[startDownload] ${err?.errorMessage}`)
          reject()
        },
      })
    })
  }

  checkDownload(ticket: number): Promise<boolean> {
    return new Promise((resolve, reject) => {
      idcap.request('idcap://storage/file/downloadstatus', {
        parameters: {
          ticket: ticket,
        },
        onSuccess: (obj: any) => {
          switch (obj.status) {
            case 'downloading':
              this.logService.debug(
                `[checkDownload] ${obj.source} ${obj.amountReceived}/${obj.amountTotal} [${Math.round((obj.amountReceived / obj.amountTotal) * 100)}%]`
              )
              return resolve(false)
            case 'completed':
              return resolve(true)
            case 'paused':
              this.logService.debug(`[checkDownload] ${ticket} ${obj.source} paused`)
              return reject()
            case 'cancelled':
              this.logService.debug(`[checkDownload] ${ticket} ${obj.source} cancelled`)
              return reject()
            case 'failed':
              this.logService.debug(`[checkDownload] ${ticket} ${obj.source} failed`)
              return reject()
            default:
              this.logService.debug(`[checkDownload] ${ticket} ${obj.source} unknown status: ${obj.status}`)
              return reject()
          }
        },
        onFailure: (err: any) => {
          this.logService.error(`[checkDownload] ${err?.errorMessage}`)
          reject()
        },
      })
    })
  }

  removeFile(path: string): Promise<void> {
    return new Promise((resolve, reject) => {
      idcap.request('idcap://storage/file/remove', {
        parameters: {
          path: path,
        },
        onSuccess: () => {
          this.logService.info('[removeFile] success')
          resolve()
        },
        onFailure: (err: any) => {
          this.logService.error(`[removeFile] ${err?.errorMessage}`)
          reject()
        },
      })
    })
  }

  getVolume(): Promise<number> {
    return new Promise((resolve, reject) => {
      idcap.request('idcap://audio/volumelevel/get', {
        parameters: {
          speakerType: 'tv_speaker',
        },
        onSuccess: (obj: any) => {
          resolve(obj.level)
        },
        onFailure: (err: any) => {
          this.logService.error(`[getVolume] ${err?.errorMessage}`)
          reject()
        },
      })
    })
  }

  setVolume(volume: number): Promise<number> {
    return new Promise((resolve, reject) => {
      idcap.request('idcap://audio/volumelevel/set', {
        parameters: {
          level: volume,
        },
        onSuccess: () => {
          this.logService.debug('[setVolume] success')
          resolve(volume)
        },
        onFailure: (err: any) => {
          this.logService.error(`[setVolume] ${err?.errorMessage}`)
          reject()
        },
      })
    })
  }

  getVolumeMute(): Promise<boolean> {
    return new Promise((resolve, reject) => {
      idcap.request('idcap://audio/volumelevel/get', {
        parameters: {
          speakerType: 'tv_speaker',
        },
        onSuccess: (obj: any) => {
          resolve(obj.muted)
        },
        onFailure: (err: any) => {
          this.logService.error(`[getVolumeMute] ${err?.errorMessage}`)
          reject()
        },
      })
    })
  }

  setVolumeMute(muted: boolean): Promise<boolean> {
    return new Promise((resolve, reject) => {
      idcap.request('idcap://audio/mute/set', {
        parameters: {
          muted: muted,
        },
        onSuccess: () => {
          this.logService.debug(`[setVolumeMute] muted = ${muted}`)
          resolve(muted)
        },
        onFailure: (err: any) => {
          this.logService.error(`[setVolumeMute] ${err?.errorMessage}`)
          reject()
        },
      })
    })
  }

  getExternalInputs(): Promise<ExternalInputData[]> {
    return new Promise((resolve, reject) => {
      idcap.request('idcap://externalinput/inputlist/get', {
        parameters: {},
        onSuccess: (obj: WebosIdcapExternalInputList) => {
          resolve(
            obj.inputSourceList.map(e => ({
              id: e.inputPort,
              type: this.getExternalInputType(e.type),
              index: e.index,
              number: this.getNumberFromInputPort(e.inputPort),
              connected: e.signalDetection,
            }))
          )
        },
        onFailure: (err: any) => {
          this.logService.error(`[getExternalInputs] ${err?.errorMessage}`)
          reject()
        },
      })
    })
  }

  private externalInputSubject?: BehaviorSubject<ExternalInputData[]>

  private readonly onInputlistStatusChanged = (param: any): void => {
    const inputSourceList = param?.inputSourceList ?? []
    const externalInputs = inputSourceList.map((e: { inputPort: string; signalDetection: boolean }) => ({
      id: e.inputPort,
      connected: e.signalDetection,
    }))
    this.externalInputSubject?.next(externalInputs)
  }

  registerExternalInputListener(subject: BehaviorSubject<ExternalInputData[]>): void {
    this.externalInputSubject = subject
    document.addEventListener('idcap::inputlist_status_changed', this.onInputlistStatusChanged, false)
  }

  private getExternalInputType(type: string): ExternalInputType {
    switch (type) {
      case 'TV':
        return ExternalInputType.TV
      case 'COMPOSITE':
        return ExternalInputType.COMPOSITE
      case 'SVIDEO':
        return ExternalInputType.SVIDEO
      case 'COMPONENT':
        return ExternalInputType.COMPONENT
      case 'RGB':
        return ExternalInputType.RGB_DVI
      case 'HDMI':
        return ExternalInputType.HDMI
      case 'SCART':
        return ExternalInputType.SCART
      case 'OTHERS':
        return ExternalInputType.OTHERS
      default:
        return ExternalInputType.OTHERS
    }
  }

  private getNumberFromInputPort(inputPort: string): number {
    const match = inputPort.match(/:(\d+)$/)
    if (match) {
      try {
        return parseInt(match[1], 10)
      } catch (err) {
        return 0
      }
    } else {
      return 0
    }
  }

  setExternalInput(type: string, index: number): Promise<void> {
    return new Promise((resolve, reject) => {
      idcap.request('idcap://externalinput/set', {
        parameters: {
          type: type,
          index: index,
        },
        onSuccess: () => {
          this.logService.debug(`[setExternalInput] success`)
          resolve()
        },
        onFailure: (err: any) => {
          this.logService.error(`[setExternalInput] ${err?.errorMessage}`)
          reject()
        },
      })
    })
  }

  setVideoSize(pos: ChannelPosition): Promise<void> {
    return new Promise((resolve, reject) => {
      idcap.request('idcap://video/size/set', {
        parameters: {
          x: pos.left,
          y: pos.top,
          width: pos.width,
          height: pos.height,
        },
        onSuccess: () => {
          this.logService.debug(`[setVideoSize] success`)
          resolve()
        },
        onFailure: (err: any) => {
          this.logService.error(`[setVideoSize] ${err?.errorMessage}`)
          reject()
        },
      })
    })
  }

  startBroadcastChannel(source: DVBTChannel): void {
    const parameters = {
      channelType: 'rf',
      frequency: source.freqHz,
      programNumber: source.serviceId,
      rfBroadcastType: 'terrestrial',
    }
    idcap.request('idcap://tv/channel/change/request', {
      parameters: parameters,
      onSuccess: () => {
        this.logService.debug(`[startBroadcastChannel] success`)
      },
      onFailure: (err: any) => {
        this.logService.error(`[startBroadcastChannel] ${err?.errorMessage}`)
      },
    })
  }

  startUDPChannel(source: UDPChannel): void {
    const parameters = {
      channelType: 'ip',
      ip: source.ip,
      port: source.port,
      ipBroadcastType: source.rtp ? 'rtp' : 'udp',
    }
    idcap.request('idcap://tv/channel/change/request', {
      parameters: parameters,
      onSuccess: () => {
        this.logService.debug(`[startUDPChannel] success`)
      },
      onFailure: (err: any) => {
        this.logService.error(`[startUDPChannel] ${err?.errorMessage}`)
      },
    })
  }

  startWebChannel(source: WebChannel): void {
    idcap.request('idcap://tv/media/create', {
      parameters: {
        url: source.url,
        mimeType: 'application/x-mpegURL',
      },
      onSuccess: () => {
        this.logService.debug(`(idcap://tv/media/create) success`)

        idcap.request('idcap://tv/media/control', {
          parameters: {
            command: 'play',
          },
          onSuccess: () => {
            this.logService.debug(`(idcap://tv/media/control) [play] success`)
          },
          onFailure: (err: any) => {
            this.logService.error(`(idcap://tv/media/control) [play] ${err?.errorMessage}`)
          },
        })
      },
      onFailure: (err: any) => {
        this.logService.error(`(idcap://tv/media/create) ${err?.errorMessage}`)
      },
    })
  }

  stopChannel(): void {
    idcap.request('idcap://tv/channel/stop', {
      onSuccess: () => {
        this.logService.debug(`[stopChannel] success`)
      },
      onFailure: (err: any) => {
        this.logService.error(`[stopChannel] ${err?.errorMessage}`)
      },
    })
  }

  stopWebChannel(): Promise<void> {
    return new Promise((resolve, reject) => {
      idcap.request('idcap://tv/media/control', {
        parameters: {
          command: 'stop',
        },
        onSuccess: () => {
          this.logService.debug(`(idcap://tv/media/control) [stop] success`)

          idcap.request('idcap://tv/media/destroy', {
            parameters: {},
            onSuccess: () => {
              this.logService.debug(`(idcap://tv/media/destroy) success`)
              resolve()
            },
            onFailure: (err: any) => {
              this.logService.error(`(idcap://tv/media/destroy) ${err?.errorMessage}`)
              reject()
            },
          })
        },
        onFailure: (err: any) => {
          this.logService.error(`(idcap://tv/media/control) [stop] ${err?.errorMessage}`)
          reject()
        },
      })
    })
  }

  startupMediaAPI(): Promise<void> {
    return new Promise((resolve, reject) => {
      idcap.request('idcap://tv/media/startup', {
        parameters: {},
        onSuccess: () => {
          this.logService.debug(`(idcap://tv/media/startup) success`)
          resolve()
        },
        onFailure: (err: any) => {
          this.logService.error(`(idcap://tv/media/startup) ${err?.errorMessage}`)
          reject()
        },
      })
    })
  }

  shutdownMediaAPI(): Promise<void> {
    return new Promise((resolve, reject) => {
      idcap.request('idcap://tv/media/shutdown', {
        parameters: {},
        onSuccess: () => {
          this.logService.debug(`(idcap://tv/media/shutdown) success`)
          setTimeout(() => {
            resolve()
          }, 500)
        },
        onFailure: (err: any) => {
          this.logService.error(`(idcap://tv/media/shutdown) ${err?.errorMessage}`)
          reject()
        },
      })
    })
  }

  getCurrentChannel(): Promise<any> {
    return new Promise((resolve, reject) => {
      idcap.request('idcap://tv/channel/get', {
        parameters: {},
        onSuccess: (obj: any) => {
          this.logService.debug(`[getCurrentChannel] success`)
          resolve(obj)
        },
        onFailure: (err: any) => {
          this.logService.error(`[getCurrentChannel] ${err?.errorMessage}`)
          reject()
        },
      })
    })
  }

  getAudioLanguageList(): Promise<string[]> {
    return new Promise((resolve, reject) => {
      idcap.request('idcap://tv/channel/audiolanguagelist/get', {
        parameters: {},
        onSuccess: (obj: any) => {
          this.logService.debug(`[getAudioLanguageList] ${obj.list}`)
          resolve(obj.list.split(','))
        },
        onFailure: (err: any) => {
          this.logService.error(`[getAudioLanguageList] ${err?.errorMessage}`)
          reject()
        },
      })
    })
  }

  getAudioLanguageCurrentIndex(): Promise<number> {
    return new Promise((resolve, reject) => {
      idcap.request('idcap://tv/channel/audiolanguageindex/get', {
        parameters: {},
        onSuccess: (obj: any) => {
          this.logService.debug(`[getAudioLanguageCurrentIndex] ${obj.index}`)
          resolve(obj.index)
        },
        onFailure: (err: any) => {
          this.logService.error(`[getAudioLanguageCurrentIndex] ${err?.errorMessage}`)
          reject()
        },
      })
    })
  }

  setAudioLanguageCurrentIndex(index: number): void {
    idcap.request('idcap://tv/channel/audiolanguageindex/set', {
      parameters: {
        index: index,
      },
      onSuccess: () => {
        this.logService.debug(`[setAudioLanguageCurrentIndex] Successfully set audio index to ${index}`)
      },
      onFailure: (err: any) => {
        this.logService.error(`[setAudioLanguageCurrentIndex] ${err?.errorMessage}`)
      },
    })
  }

  getSubtitleList(): Promise<string[]> {
    return new Promise((resolve, reject) => {
      idcap.request('idcap://tv/channel/subtitlelist/get', {
        parameters: {},
        onSuccess: (obj: any) => {
          this.logService.debug(`[getSubtitleList] ${obj.list}`)
          resolve(obj.list.split(','))
        },
        onFailure: (err: any) => {
          this.logService.error(`[getSubtitleList] ${err?.errorMessage}`)
          reject()
        },
      })
    })
  }

  getSubtitleCurrentIndex(): Promise<number> {
    return new Promise((resolve, reject) => {
      idcap.request('idcap://tv/channel/subtitleindex/get', {
        parameters: {},
        onSuccess: (obj: any) => {
          this.logService.debug(`[getSubtitleCurrentIndex] ${obj.index}`)
          resolve(obj.index)
        },
        onFailure: (err: any) => {
          this.logService.error(`[getSubtitleCurrentIndex] ${err?.errorMessage}`)
          reject()
        },
      })
    })
  }

  setSubtitleCurrentIndex(index: number): void {
    idcap.request('idcap://tv/channel/subtitleindex/set', {
      parameters: {
        index: index,
      },
      onSuccess: () => {
        this.logService.debug(`[setSubtitleCurrentIndex] Successfully set subtitle index to ${index}`)
      },
      onFailure: (err: any) => {
        this.logService.error(`[setSubtitleCurrentIndex] ${err?.errorMessage}`)
      },
    })
  }

  getMediaAudioLanguage(): Promise<{ index: number; list: string[] }> {
    return new Promise((resolve, reject) => {
      idcap.request('idcap://tv/media/audiolanguage/get', {
        parameters: {},
        onSuccess: (obj: any) => {
          this.logService.debug(`[getMediaAudioLanguage] ${safeStringify(obj)}`)
          const list = typeof obj.list === 'string' && obj.list.length > 0 ? obj.list.split(',') : []
          resolve({ index: obj.index, list })
        },
        onFailure: (err: any) => {
          this.logService.error(`[getMediaAudioLanguage] ${err?.errorMessage}`)
          reject()
        },
      })
    })
  }

  setMediaAudioLanguageIndex(index: number): void {
    idcap.request('idcap://tv/media/audiolanguage/set', {
      parameters: {
        index: index,
      },
      onSuccess: () => {
        this.logService.debug(`[setMediaAudioLanguageIndex] Successfully set audio index to ${index}`)
        this.seekToCurrentMediaPosition()
      },
      onFailure: (err: any) => {
        this.logService.error(`[setMediaAudioLanguageIndex] ${err?.errorMessage}`)
      },
    })
  }

  // Seeking to the current position makes the player drop its buffered audio, so a new audio language is heard right away. Not used for subtitles: seeking on each activation stacks the subtitles on screen
  private seekToCurrentMediaPosition(): void {
    idcap.request('idcap://tv/media/playposition/get', {
      parameters: {},
      onSuccess: (obj: any) => {
        idcap.request('idcap://tv/media/playposition/set', {
          parameters: {
            positionInMs: obj.positionInMs,
          },
          onSuccess: () => {
            this.logService.debug(`[seekToCurrentMediaPosition] Successfully sought to ${obj.positionInMs}ms`)
          },
          onFailure: (err: any) => {
            this.logService.error(`[seekToCurrentMediaPosition] ${err?.errorMessage}`)
          },
        })
      },
      onFailure: (err: any) => {
        this.logService.error(`[seekToCurrentMediaPosition] ${err?.errorMessage}`)
      },
    })
  }

  getMediaSubtitle(): Promise<{ type: string; index: number; list: string[] }> {
    return new Promise((resolve, reject) => {
      idcap.request('idcap://tv/media/subtitle/get', {
        parameters: {},
        onSuccess: (obj: any) => {
          this.logService.debug(`[getMediaSubtitle] ${safeStringify(obj)}`)
          const list = typeof obj.list === 'string' && obj.list.length > 0 ? obj.list.split(',') : []
          resolve({ type: obj.type, index: obj.index, list })
        },
        onFailure: (err: any) => {
          this.logService.error(`[getMediaSubtitle] ${err?.errorMessage}`)
          reject()
        },
      })
    })
  }

  setMediaSubtitle(type: string, index: number): Promise<void> {
    return new Promise((resolve, reject) => {
      idcap.request('idcap://tv/media/subtitle/set', {
        parameters: {
          type: type,
          index: index,
        },
        onSuccess: () => {
          this.logService.debug(`[setMediaSubtitle] Successfully set subtitle ${type} #${index}`)
          resolve()
        },
        onFailure: (err: any) => {
          this.logService.error(`[setMediaSubtitle] ${err?.errorMessage}`)
          reject()
        },
      })
    })
  }

  setMediaSubtitleOn(subtitleOn: boolean): Promise<void> {
    return new Promise((resolve, reject) => {
      idcap.request('idcap://tv/media/subtitleon/set', {
        parameters: {
          subtitleOn: subtitleOn,
        },
        onSuccess: () => {
          this.logService.debug(`[setMediaSubtitleOn] Successfully set subtitle display to ${subtitleOn}`)
          resolve()
        },
        onFailure: (err: any) => {
          this.logService.error(`[setMediaSubtitleOn] ${err?.errorMessage}`)
          reject()
        },
      })
    })
  }

  getAppList(): Promise<any[]> {
    return new Promise((resolve, reject) => {
      idcap.request('idcap://application/list', {
        parameters: {},
        onSuccess: (obj: any) => {
          this.logService.debug(`[getAppList] success`)
          resolve(obj.list)
        },
        onFailure: (err: any) => {
          this.logService.error(`[getAppList] ${err?.errorMessage}`)
          reject()
        },
      })
    })
  }

  startApp(appId: string, params: object, noSplash: boolean): Promise<void> {
    return new Promise((resolve, reject) => {
      idcap.request('idcap://application/launch', {
        parameters: {
          id: appId,
          params: params,
          noSplash: noSplash,
        },
        onSuccess: () => {
          this.logService.debug(`[startApp] success`)
          resolve()
        },
        onFailure: (err: any) => {
          this.logService.error(`[startApp] ${err?.errorMessage}`)
          reject()
        },
      })
    })
  }

  stopApp(appId: string): void {
    idcap.request('idcap://application/destroy', {
      parameters: {
        id: appId,
      },
      onSuccess: () => {
        this.logService.debug(`[stopApp] success`)
      },
      onFailure: (err: any) => {
        this.logService.error(`[stopApp] ${err?.errorMessage}`)
      },
    })
  }

  installApps(appIds: string[]): Promise<void> {
    return new Promise((resolve, reject) => {
      idcap.request('idcap://application/install', {
        parameters: {
          appList: appIds,
        },
        onSuccess: () => {
          this.logService.debug(`[installApps] success`)
          resolve()
        },
        onFailure: (err: any) => {
          this.logService.error(`[installApps] ${err?.errorMessage}`)
          reject()
        },
      })
    })
  }

  uninstallApps(appIds: string[]): void {
    idcap.request('idcap://application/uninstall', {
      parameters: {
        appList: appIds,
      },
      onSuccess: () => {
        this.logService.debug(`[uninstallApps] success`)
      },
      onFailure: (err: any) => {
        this.logService.error(`[uninstallApps] ${err?.errorMessage}`)
      },
    })
  }

  getCurrentApp(): Promise<{ appId: string }> {
    return new Promise((resolve, reject) => {
      idcap.request('idcap://application/foregroundapp/get', {
        parameters: {},
        onSuccess: (obj: any) => {
          this.logService.debug(`[getCurrentApp] success`)
          resolve(obj)
        },
        onFailure: (err: any) => {
          this.logService.error(`[getCurrentApp] ${err?.errorMessage}`)
          reject()
        },
      })
    })
  }

  registerAppToken(tokenList: { id: string; token: string }[]): Promise<void> {
    return new Promise((resolve, reject) => {
      idcap.request('idcap://application/register', {
        parameters: {
          tokenList: tokenList,
        },
        onSuccess: () => {
          this.logService.debug(`[registerAppToken] success`)
          resolve()
        },
        onFailure: (err: any) => {
          this.logService.error(`[registerAppToken] ${err?.errorMessage}`)
          reject()
        },
      })
    })
  }

  private applicationInstalledSubject?: BehaviorSubject<string[]>

  private readonly onApplicationInstalled = (param: any): void => {
    const appIds = param?.successList ?? []
    this.applicationInstalledSubject?.next(appIds)
  }

  registerApplicationInstalledListener(subject: BehaviorSubject<string[]>): void {
    this.applicationInstalledSubject = subject
    document.addEventListener('idcap::application_installed', this.onApplicationInstalled, false)
  }

  private applicationFocusSubject?: BehaviorSubject<boolean>

  private readonly onApplicationFocusChanged = (param: any): void => {
    this.applicationFocusSubject?.next(param.eventType == 'focused')
  }

  registerApplicationFocusListener(subject: BehaviorSubject<boolean>): void {
    this.applicationFocusSubject = subject
    document.addEventListener('idcap::idcap_application_focus_changed', this.onApplicationFocusChanged, false)
  }

  private channelChangedSubject?: BehaviorSubject<boolean>

  private readonly onChannelChanged = (param: any): void => {
    this.channelChangedSubject?.next(param.result)
  }

  private readonly onMediaEventReceived = (param: any): void => {
    this.logService.debug(`[onMediaEventReceived] ${safeStringify(param)}`)
    if (param?.eventType === 'play_start') {
      this.channelChangedSubject?.next(true)
    }
  }

  registerChannelChangedListener(subject: BehaviorSubject<boolean>): void {
    this.channelChangedSubject = subject
    document.addEventListener('idcap::channel_changed', this.onChannelChanged, false)
    document.addEventListener('idcap::media_event_received', this.onMediaEventReceived, false)
  }

  getLocale(): Promise<string> {
    return new Promise((resolve, reject) => {
      idcap.request('idcap://configuration/locale/get', {
        parameters: {},
        onSuccess: (obj: any) => {
          resolve(obj.specifier)
        },
        onFailure: (err: any) => {
          this.logService.error(`[locale_get] ${err?.errorMessage}`)
          reject()
        },
      })
    })
  }

  setLocale(specifier: string): Promise<void> {
    return new Promise((resolve, reject) => {
      idcap.request('idcap://configuration/locale/set', {
        parameters: {
          specifier: specifier,
        },
        onSuccess: () => {
          this.logService.debug(`[locale_set] success`)
          resolve()
        },
        onFailure: (err: any) => {
          this.logService.error(`[locale_set] ${err?.errorMessage}`)
          reject()
        },
      })
    })
  }

  getLocaleList(): Promise<string[]> {
    return new Promise((resolve, reject) => {
      idcap.request('idcap://configuration/locale/list', {
        parameters: {},
        onSuccess: (obj: any) => {
          resolve(obj.list.map((e: { specifier: any }) => e.specifier))
        },
        onFailure: (err: any) => {
          this.logService.error(`[locale_list] ${err?.errorMessage}`)
          reject()
        },
      })
    })
  }
}
